<template>
    <div class="settings-modal">
        <Modal @cancel="modalState.close" @confirm="modalState.save">
            <div class="modal-content">
                <div class="headline-container">
                    <Icon v-if="modalState.setting['icon']" :name="modalState.setting['icon']" :size="20" />
                    <div class="copy-l bold">{{ modalState.setting['empty'] }}</div>
                </div>
                <div v-if="mounted" class="main">
                    <DInput
                        v-if="validDefaultInputTypes.includes(modalState.setting.type)"
                        v-model="modalValue"
                        :type="modalState.setting.type"
                        :placeholder="modalState.setting['description']"
                    >
                        <template v-if="modalState.setting.suffix" #suffix>
                            <span class="copy-m">{{ modalState.setting.suffix }}</span>
                        </template>
                        <template v-if="modalState.setting.prefix" #prefix>
                            <span class="copy-m">{{ modalState.setting.prefix }}</span>
                        </template>
                    </DInput>
                    <Select
                        v-if="modalState.setting.type === 'select'"
                        v-model="modalValue"
                        :placeholder="modalState.setting['description']"
                        :options="modalState.setting.selectOptions"
                    >
                    </Select>

                    <div v-if="modalState.setting.type === 'date-range'" class="date-range">date-range</div>

                    <div v-if="modalState.setting.type === 'tags'" class="tags">
                        <div ref="select" class="input container">
                            <DInput
                                v-model="tagSearch"
                                type="text"
                                :placeholder="modalState.setting['description']"
                                @focus="showSelectOptions = true"
                            />
                            <div v-show="showSelectOptions && filteredSelectOptions.length > 0" class="select-options">
                                <div
                                    v-for="selectOption in filteredSelectOptions"
                                    :key="selectOption.value"
                                    class="select-option copy-m"
                                    tabindex="0"
                                    @click.stop="addTag(selectOption.value)"
                                    @keydown.enter.stop="addTag(selectOption.value)"
                                >
                                    {{ selectOption.value }}
                                </div>
                            </div>
                        </div>
                        <div v-if="tagItems.length > 0" class="tag-items">
                            <Tag v-for="item in tagItems" :key="item" :value="item" removable @remove="removeTag" />
                        </div>
                    </div>

                    <div class="description copy-s">
                        {{ $t('event.settings_modal.at_public_event') }}<br />
                        {{ $t('event.settings_modal.immediately_visible') }}
                    </div>
                </div>
                <div class="action-container fullwidth">
                    <Button type="secondary" :value="$t('general.cancel')" @click="modalState.close" />
                    <Button type="primary" :value="$t('general.save')" @click="modalState.save" />
                </div>
            </div>
        </Modal>
    </div>
</template>

<script>
import Modal from '@/components/Modal'
import Icon from '@/components/Icon'
import Button from '@/components/Button'
import DInput from '@/components/Input'
import Select from '@/components/Select'
import Tag from '@/components/Tag'
import validHTMLInputTypes from '@/validHTMLInputTypes'

export default {
    name: 'SettingsModal',
    components: {
        Modal,
        Icon,
        Button,
        DInput,
        Select,
        Tag,
    },
    props: {
        modalOpen: {
            type: Boolean,
        },
        setting: {
            type: Object,
        },
        preValue: {
            type: [String, Number],
        },
    },
    data() {
        return {
            mounted: false,
            tagSearch: '',
            lSetting: this.setting,
            modalValue: '',
            showSelectOptions: false,
            validDefaultInputTypes: validHTMLInputTypes,
        }
    },
    computed: {
        filteredSelectOptions() {
            if (this.modalState.setting.selectOptions) {
                return (
                    this.modalState.setting.selectOptions
                        // filter by search term
                        .filter((x) => x.text.toLowerCase().includes(this.tagSearch.toLowerCase()))
                        // remove already added items
                        .filter((el) => !this.tagItems.includes(el.text))
                )
            }
            return []
        },
        modalState() {
            return !this.lSetting
                ? false
                : {
                      setting: this.lSetting,
                      close: () => this.closeSetting(),
                      save: () => this.saveSetting(),
                  }
        },
        tagItems() {
            if (this.lSetting.type === 'tags') {
                if (this.lSetting.value === '') {
                    return []
                } else {
                    if (this.lSetting.value instanceof Array) {
                        return this.lSetting.value
                    } else {
                        return this.lSetting.value.split(',')
                    }
                }
            }
            return []
        },
    },
    mounted() {
        window.addEventListener('click', this.autoClose)

        this.modalValue = this.setting.value
        if (!this.modalValue && this.preValue !== null) {
            this.lSetting.value = this.preValue.toString()
        }

        if (this.modalValue === '' && this.preValue !== null) {
            this.modalValue = this.preValue.toString()
        }

        this.mounted = true
    },
    beforeDestroy() {
        window.removeEventListener('click', this.autoClose)
    },
    methods: {
        autoClose(e) {
            if (!e.composedPath().includes(this.$refs['select'])) this.showSelectOptions = false
        },
        addTag(tag) {
            this.tagSearch = ''
            let genres = this.tagItems
            genres.push(tag)

            // remove duplicates
            genres = [...new Set(genres)]

            this.lSetting.value = genres.join(',')
        },
        removeTag(tag) {
            let genres = this.tagItems
            const index = genres.indexOf(tag)
            if (index > -1) {
                genres.splice(index, 1)
            }
            this.lSetting.value = genres.join(',')
        },
        saveInterceptor() {
            this.selectInterceptor()
            this.tagsInterceptor()
            this.numberInterceptor()
        },
        selectInterceptor() {
            if (this.lSetting.type === 'select') {
                let option = this.lSetting['selectOptions'].find((x) => x.value === this.modalValue)
                this.modalValue = option.text
            }
        },
        tagsInterceptor() {
            if (this.lSetting.type === 'tags') {
                this.modalValue = this.tagItems
                this.lSetting['closeModal'] = false
            }
        },
        numberInterceptor() {
            if (this.lSetting.type === 'number') {
                this.modalValue = Number(this.modalValue)
            }
        },
        closeSetting() {
            this.$emit('close')
        },
        saveSetting() {
            this.saveInterceptor()
            this.lSetting.value = this.modalValue
            this.$emit('save', this.lSetting)
        },
    },
}
</script>

<style scoped lang="sass">
.settings-modal
    .main
        .tags
            width: 420px

            .input
                margin-bottom: $xs

            .select-options
                max-height: 100px
                overflow-y: scroll
                border-radius: 5px
                margin-bottom: $s
                box-shadow: 0px 0px 0px rgba(6, 44, 82, 0.1), 0px 1px 3px rgba(64, 66, 69, 0.12), 0px 2px 16px rgba(33, 43, 54, 0.08)
                padding: $s $xxs

                .select-option
                    display: flex
                    min-width: 0
                    align-items: center
                    height: 3.2rem
                    padding: 0 $s
                    cursor: pointer
                    border:
                        radius: 5px
                    &:hover
                        background: $gray-1
        .tag-items
            margin-top: $s

            .tag
                margin-bottom: $xs
                margin-left: 0
                margin-right: $xs
</style>
